import React from "react";
import { formatMoney, formatNumber } from "../menuEngineeringUtils";
import { RANK_ACTIONS } from "./menuDashboardInsights";

// Glyphs from Phosphor Icons (MIT), viewBox 0 0 256 256, matching the reference design.
const RANK_PATHS = {
  Star: "M208,40H48A16,16,0,0,0,32,56v56c0,52.72,25.52,84.67,46.93,102.19,23.06,18.86,46,25.26,47,25.53a8,8,0,0,0,4.2,0c1-.27,23.91-6.67,47-25.53C198.48,196.67,224,164.72,224,112V56A16,16,0,0,0,208,40Zm-34.32,69.66-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35a8,8,0,0,1,11.32,11.32Z",
  Plowhorse: "M243.31,136,144,36.69A15.86,15.86,0,0,0,132.69,32H40a8,8,0,0,0-8,8v92.69A15.86,15.86,0,0,0,36.69,144L136,243.31a16,16,0,0,0,22.63,0l84.68-84.68a16,16,0,0,0,0-22.63ZM84,96A12,12,0,1,1,96,84,12,12,0,0,1,84,96Z",
  Puzzle: "M200,72H160.2c-2.91-.17-53.62-3.74-101.91-44.24A16,16,0,0,0,32,40V200a16,16,0,0,0,26.29,12.25c37.77-31.68,77-40.76,93.71-43.3v31.72A16,16,0,0,0,159.12,214l11,7.33A16,16,0,0,0,194.5,212l11.77-44.36A48,48,0,0,0,200,72ZM179,207.89l0,.11-11-7.33V168h21.6ZM200,152H168V88h32a32,32,0,1,1,0,64Z",
  Dog: "M228,48V96a12,12,0,0,1-12,12H168a12,12,0,0,1,0-24h19l-7.8-7.8a75.55,75.55,0,0,0-53.32-22.26h-.43A75.49,75.49,0,0,0,72.39,75.57,12,12,0,1,1,55.61,58.41a99.38,99.38,0,0,1,69.87-28.47H126A99.42,99.42,0,0,1,196.2,59.23L204,67V48a12,12,0,0,1,24,0ZM183.61,180.43a75.49,75.49,0,0,1-53.09,21.63h-.43A75.55,75.55,0,0,1,76.77,179.8L69,172H88a12,12,0,0,0,0-24H40a12,12,0,0,0-12,12v48a12,12,0,0,0,24,0V189l7.8,7.8A99.42,99.42,0,0,0,130,226.06h.56a99.38,99.38,0,0,0,69.87-28.47,12,12,0,0,0-16.78-17.16Z",
};

const LEGEND_COPY = {
  Star: "Popular and profitable. Keep it exactly where it is, and never discount it.",
  Plowhorse: "Popular but thin margin. Raise the price a little, or bring the recipe cost down.",
  Puzzle: "Profitable but overlooked. Give it a better spot on the menu and ask staff to suggest it.",
  Dog: "Neither popular nor profitable. Replace it, unless it's a signature dish.",
};

const REC_TAGS = { promote: "Promote", recost: "Re-cost", reprice: "Reprice", review: "Review", cost: "Add cost" };

export function RankIcon({ rank, size = 14 }) {
  if (!RANK_PATHS[rank]) return null;
  return (
    <svg className="menuRankIcon" aria-hidden="true" width={size} height={size} viewBox="0 0 256 256" fill="currentColor">
      <path d={RANK_PATHS[rank]} />
    </svg>
  );
}

export function RankBadge({ rank }) {
  return (
    <span className={`menuRankBadge rank-${rank.toLowerCase()}`}>
      <RankIcon rank={rank} size={12} />{rank}
    </span>
  );
}

const SUMMARY_RANKS = [["Star", "Stars"], ["Puzzle", "Puzzles"], ["Plowhorse", "Plowhorses"], ["Dog", "Dogs"]];

// Count and share of total contribution for each class in the current view.
export function ClassSummary({ items }) {
  const totalProfit = items.reduce((sum, item) => sum + (Number(item.totalProfit) || 0), 0);
  return (
    <div className="menuClassSummary" aria-label="Class summary">
      {SUMMARY_RANKS.map(([rank, label]) => {
        const rankItems = items.filter((item) => item._rank === rank);
        const profit = rankItems.reduce((sum, item) => sum + (Number(item.totalProfit) || 0), 0);
        const share = totalProfit > 0 ? Math.max(0, Math.round((profit / totalProfit) * 100)) : 0;
        return (
          <div key={rank} className={`menuClassSummaryTile rank-${rank.toLowerCase()}`}>
            <div className="menuClassSummaryHead"><span>{label}</span><strong>{rankItems.length}</strong></div>
            <small>{share}% of contribution</small>
          </div>
        );
      })}
    </div>
  );
}

export function Legend({ popularityLine, marginLine, qtyNeeded, days }) {
  return (
    <section className="menuCard menuLegend" aria-label="How to read this">
      <div className="menuCardHeader">
        <div><h2>How to read this</h2><p>Every dish lands in one of four groups. Each group has one action.</p></div>
      </div>
      <div className="menuLegendTiles">
        {["Star", "Plowhorse", "Puzzle", "Dog"].map((rank) => (
          <div key={rank} className={`menuLegendTile rank-${rank.toLowerCase()}`}>
            <div className="menuLegendTop">
              <span className="menuLegendIcon"><RankIcon rank={rank} size={22} /></span>
              <strong>{rank}</strong>
            </div>
            <div className="menuLegendAction">{RANK_ACTIONS[rank]}</div>
            <p>{LEGEND_COPY[rank]}</p>
          </div>
        ))}
      </div>
      <div className="menuLegendLines">
        <p><strong>Popularity line, {formatNumber(popularityLine, 1)}%.</strong> A dish counts as popular when it sells at least 70% of its fair share (an equal split of sales across every dish). For this outlet the line is {formatNumber(popularityLine, 1)}%: at least <strong>{formatNumber(qtyNeeded)}</strong> plates{days ? ` in ${days} day${days === 1 ? "" : "s"}` : ""}.</p>
        <p><strong>Margin line, {formatMoney(marginLine)}.</strong> A dish counts as profitable when it earns at least the average margin per plate (what's left after the recipe cost, across every plate sold).</p>
      </div>
      <p className="menuDashboardNote">Classifications come from the outlet's results. Dishes with no recipe cost are flagged so their margin can be reviewed.</p>
    </section>
  );
}

export function Recommendations({ recommendations, onPick, limit = 4 }) {
  const shown = recommendations.slice(0, limit);
  return (
    <section className="menuCard menuRecommendations" aria-label="Top moves">
      <div className="menuCardHeader">
        <div><h2>Top moves this period</h2><p>Ranked by contribution gained at current volume.</p></div>
      </div>
      {!shown.length ? (
        <p className="menuDashboardNote">Nothing to fix in this view. Every dish is a Star.</p>
      ) : (
        <div className="menuRecList">
          {shown.map((rec) => (
            <button type="button" key={`${rec.itemKey}-${rec.kind}`} className={`menuRec rank-${rec.rank.toLowerCase()}`} onClick={() => onPick(rec.itemKey)}>
              <span className="menuRecRow">
                <span className="menuRankBadge">{REC_TAGS[rec.kind]}</span>
                <span className="menuRecImpact">
                  {rec.impact > 0 ? `+QAR ${formatNumber(rec.impact)}` : rec.kind === "review" ? "Frees menu space" : "Unblocks decisions"}
                </span>
              </span>
              <strong>{rec.title}</strong>
              <span className="menuRecBody">{rec.body}</span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

export function CostCoverage({ coverage }) {
  const dishShare = coverage.totalItems
    ? Math.round((coverage.ratedItems / coverage.totalItems) * 100)
    : 0;
  const missingSalesShare = Math.round((1 - coverage.salesShare) * 100);
  const unratedItems = coverage.totalItems - coverage.ratedItems;
  return (
    <div className="menuCoverageCallout" role="status" aria-label="Recipe cost coverage">
      <div
        className="menuCoverageGauge"
        role="img"
        aria-label={`${dishShare}% of dishes have recipe costs`}
        style={{
          background: `conic-gradient(var(--ins-ink) 0 ${dishShare}%, var(--ins-line) ${dishShare}% 100%)`,
        }}
      >
        <span>{dishShare}%</span>
      </div>
      <div className="menuCoverageCopy">
        <strong>Cost coverage.</strong> {unratedItems} of {coverage.totalItems} dishes have no recipe cost ({missingSalesShare}% of sales). They're shown as <em>Unrated</em>, not guessed.
        {unratedItems > 0 && " Review recipe costs before acting."}
      </div>
    </div>
  );
}

export function ConfidenceCallout({ confidence, days }) {
  if (!confidence || confidence === "high") return null;
  return (
    <div className={`menuConfidence ${confidence}`} role="status">
      <strong>{confidence === "low" ? "Low confidence" : "Medium confidence"}</strong>
      <span>
        Only {days} {days === 1 ? "day" : "days"} of sales. Rankings settle at around four weeks, so treat these moves as early signals.
      </span>
    </div>
  );
}

export function Delta({ value, suffix = "%", invert = false, digits = 1 }) {
  if (value == null || !Number.isFinite(value)) return null;
  const good = invert ? value < 0 : value > 0;
  const tone = value === 0 ? "flat" : good ? "up" : "down";
  return (
    <span className={`menuDelta ${tone}`}>
      {value > 0 ? "▲" : value < 0 ? "▼" : "•"} {formatNumber(Math.abs(value), digits)}{suffix}
    </span>
  );
}
